import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import axios from "../../../axios";
import { colors, radius } from "../../../styles/theme/tokens";
import * as S from "../Funnel/Funnel.styled";

// «Контроль вчителів»: скільки уроків мало бути за розкладом активних учнів → скільки відмічено, скасовано
// і скільки зникло без пояснень; учні, з якими давно не було уроку. Дані: GET /admin/teachers/summary
const PERIODS = [7, 14, 30];
const fmt = (n) => Math.round(n).toLocaleString("uk-UA");
const day = (d) => new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
const ago = (d) => {
  if (!d) return "ще не записано";
  const days = Math.floor((Date.now() - new Date(d)) / 864e5);
  return days === 0 ? "сьогодні" : days === 1 ? "вчора" : `${days} дн тому`;
};
const tone = (t) => (t.completion === null ? null : t.completion < 60 || t.stale.length >= 2 ? "red" : t.completion < 85 || t.stale.length ? "amber" : "green");
// Порядок карток: зелені → жовті → червоні → ще без даних (нові учні); всередині — від кращого виконання до гіршого
const TONE_ORDER = { green: 0, amber: 1, red: 2 };
const byTone = (a, b) => (TONE_ORDER[tone(a)] ?? 3) - (TONE_ORDER[tone(b)] ?? 3) || (b.completion ?? -1) - (a.completion ?? -1);

export default function TeachersControl() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setError(false);
    axios.get(`admin/teachers/summary?days=${days}`).then((res) => setData(res.data)).catch(() => setError(true));
  }, [days]);

  const t = data?.totals;
  return (
    <PagesWrapper>
      <TopPageTitle value="Контроль вчителів" />
      <S.Page>
        <S.Card>
          <S.CardHead>
            <div>
              <S.H>За останні {days} днів{data ? ` (${day(data.from)} – ${day(data.to)})` : ""}</S.H>
              <S.Hint>«За розкладом» — слоти активних учнів. «Зникло» — мало бути, але немає ні відмітки, ні скасування. Сьогоднішні уроки можуть бути ще не відмічені.</S.Hint>
            </div>
            <S.Chips>
              {PERIODS.map((p) => (
                <S.Chip key={p} $on={p === days} onClick={() => setDays(p)}>{p} днів</S.Chip>
              ))}
            </S.Chips>
          </S.CardHead>
          {error && <S.ErrorText>Не вдалося завантажити зведення</S.ErrorText>}
          {t && (
            <S.Stats>
              <S.Stat><S.Big>{t.expected}</S.Big><S.Small>уроків за розкладом</S.Small></S.Stat>
              <S.Stat><S.Big>{t.done + t.noShow}</S.Big><S.Small>відмічено{t.noShow ? ` (неявок ${t.noShow})` : ""}</S.Small></S.Stat>
              <S.Stat $tone="amber"><S.Big>{t.cancelledByTeacher}</S.Big><S.Small>скасував вчитель</S.Small></S.Stat>
              <S.Stat $tone="pink"><S.Big>{t.missing}</S.Big><S.Small>зникло без пояснень</S.Small></S.Stat>
              <S.Stat $tone="pink"><S.Big>{t.stale}</S.Big><S.Small>учнів без уроку {data.staleDays}+ дн</S.Small></S.Stat>
              <S.Stat><S.Big>{fmt(t.salary)}</S.Big><S.Small>ЗП за період, грн</S.Small></S.Stat>
            </S.Stats>
          )}
        </S.Card>

        {data && (
          <Grid>
            {[...data.teachers].sort(byTone).map((x) => (
              <TeacherCard key={x._id} $tone={tone(x)}>
                <S.PersonHead>
                  <Name onClick={() => navigate(`/admin/ourteachers/${x._id}`)}>
                    {x.name} <span>· id {x.teacherId}</span>
                  </Name>
                  {x.completion === null ? <S.Tag>{x.students ? "новий учень, мало даних" : "немає учнів"}</S.Tag> : <S.Tag $c={tone(x)}>{x.completion}% уроків</S.Tag>}
                </S.PersonHead>
                <Line>
                  Учнів {x.students} · за розкладом {x.expected} · відмічено <b>{x.done + x.noShow}</b>
                  {x.noShow ? ` (неявок ${x.noShow})` : ""}{x.trials ? ` · пробних ${x.trials}` : ""}
                </Line>
                <Line>
                  {x.cancelledByTeacher > 0 && <S.Tag $c="amber">скасував сам: {x.cancelledByTeacher}</S.Tag>}{" "}
                  {x.cancelledByStudent > 0 && <S.Tag>скасував учень: {x.cancelledByStudent}</S.Tag>}{" "}
                  {x.missing > 0 && <S.Tag $c="red">зникло: {x.missing}</S.Tag>}
                </Line>
                {x.stale.length > 0 && (
                  <Stale>
                    Без уроку {data.staleDays}+ днів:{" "}
                    {x.stale.map((s, i) => (
                      <span key={s.studentId}>
                        {i > 0 && ", "}
                        <b>{s.name}</b> (ID {s.studentId}, {s.lastLessonAt ? `ост. ${day(s.lastLessonAt)}` : "уроків немає"}{s.totalLessons > 0 ? `, на балансі ${s.totalLessons}` : ""})
                      </span>
                    ))}
                  </Stale>
                )}
                <Meta>
                  ЗП {fmt(x.salary)} грн · в кабінеті {ago(x.lastSeenAt)}{x.accessBlocked ? " · 🔒 доступ закрито" : ""}
                </Meta>
              </TeacherCard>
            ))}
          </Grid>
        )}
      </S.Page>
    </PagesWrapper>
  );
}

const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr)); gap: 14px;`;
const TeacherCard = styled(S.Card)`
  display: flex; flex-direction: column; gap: 8px;
  border-top: 4px solid ${(p) => ({ red: colors.danger, amber: colors.amberDeep, green: colors.success }[p.$tone] || colors.line)};
`;
const Name = styled.button`
  border: 0; background: none; padding: 0; font: inherit; font-weight: 800; font-size: 1rem; color: ${colors.ink}; cursor: pointer; text-align: left;
  span { font-weight: 500; color: ${colors.muted}; font-size: 0.85rem; }
  &:hover { text-decoration: underline; }
`;
const Line = styled.div`font-size: 0.88rem;`;
const Stale = styled.div`font-size: 0.85rem; background: ${colors.dangerSoft}; border-radius: ${radius.md}; padding: 8px 10px;`;
const Meta = styled.div`font-size: 0.8rem; color: ${colors.muted};`;
